import { useState, useImperativeHandle, forwardRef } from "react";
import { Input, Button } from "antd";
import {
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import styles from "./index.module.scss";

interface CohortCardForArmProps {
  cohort: Digital_Protocol_Arm_Types.Cohort;
  cohortIndex: number;
  listForDuplicationCheck?: Digital_Protocol_Arm_Types.Cohort[];
  handleSaveForEdit: (
    cohort: Digital_Protocol_Arm_Types.Cohort,
    index: number
  ) => void;
  handleCancelForEdit: (index: number) => void;
  handleDel: (index: number) => void;
}

export const CohortCardForArm = forwardRef(
  (
    {
      cohort,
      cohortIndex,
      listForDuplicationCheck,
      handleSaveForEdit,
      handleCancelForEdit,
      handleDel,
    }: CohortCardForArmProps,
    ref: any
  ) => {
    const { TextArea } = Input;

    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [cohortName, setCohortName] = useState<string>(
      cohort?.cohort_name || ""
    );
    const [cohortDesc, setCohortDesc] = useState<string>(
      cohort?.cohort_description || ""
    );
    const [isEdit, setIsEdit] = useState(!Boolean(cohort?.cohort_name));
    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let tmpErrorMsg = "";
      let isValid = true;
      if (!Boolean(cohortName)) {
        isValid = false;
        tmpErrorMsg = "Cohort name";
      }
      if (!Boolean(cohortDesc)) {
        isValid = false;
        tmpErrorMsg += tmpErrorMsg
          ? " and Cohort Description"
          : "Cohort Description";
      }

      if (tmpErrorMsg) {
        const isPlural = tmpErrorMsg.includes("and");
        setErrorMsg(
          `${tmpErrorMsg} ${isPlural ? "are" : "is"} required. Please fill ${
            isPlural ? "them" : "it"
          } in.`
        );
      } else if (listForDuplicationCheck?.length > 0) {
        // cohort duplication check
        const duplicatedList = listForDuplicationCheck.filter(
          (item, index) =>
            index !== cohortIndex &&
            item.cohort_name.toLowerCase() ===
              cohortName.trim().toLowerCase() &&
            !item.del_flg
        );
        if (duplicatedList.length > 0) {
          setErrorMsg(
            "Cohort name already exists. Please enter a unique name."
          );
          isValid = false;
        } else {
          setErrorMsg("");
        }
      } else {
        setErrorMsg("");
      }
      return [isValid, isEdit];
    };

    const checkAndSubmit = () => {
      const [isValid] = doValidation();
      if (!isValid) {
        return;
      }

      handleSaveForEdit(
        {
          cohort_name: cohortName,
          cohort_description: cohortDesc,
        },
        cohortIndex
      );
      setIsEdit(false);
    };

    const cancelEdit = () => {
      handleCancelForEdit(cohortIndex);
      setCohortName(cohort?.cohort_name || "");
      setCohortDesc(cohort?.cohort_description || "");
      setIsEdit(false);
    };

    return (
      <>
        <div className={styles.cardRoot}>
          <div className={styles.header}>
            {!isEdit ? (
              <>
                <div className={styles.title}>{cohortName}</div>
                <div className={styles.btns}>
                  <EditOutlined
                    className={styles.delIcon}
                    onClick={() => setIsEdit(true)}
                  />
                  <DeleteOutlined
                    className={styles.delIcon}
                    onClick={() => handleDel(cohortIndex)}
                  />
                </div>
              </>
            ) : (
              <Input
                placeholder="Cohort Name *"
                autoFocus
                maxLength={50}
                value={cohortName}
                onChange={(e) => setCohortName(e.target.value)}
              />
            )}
          </div>

          <div className={styles.body}>
            {!isEdit ? (
              <span>{cohortDesc}</span>
            ) : (
              <>
                <TextArea
                  placeholder="Description *"
                  showCount
                  maxLength={100}
                  autoSize={{ minRows: 2, maxRows: 2 }}
                  value={cohortDesc}
                  onChange={(e) => setCohortDesc(e.target.value)}
                />
              </>
            )}
          </div>
          <span className={styles.errorMsg}>{errorMsg}</span>

          {isEdit && (
            <div className={styles.btns}>
              <Button
                className={styles.iconBtn}
                icon={<CheckOutlined />}
                onClick={checkAndSubmit}
              />
              <Button
                className={styles.iconBtn}
                icon={<CloseOutlined />}
                onClick={cancelEdit}
              />
            </div>
          )}
        </div>
      </>
    );
  }
);
